import type { MisconceptionDefinition } from './types'

/** GET — Getalbegrip misconcepties. */
export const GET_MISCONCEPTIONS: MisconceptionDefinition[] = [
  {
    id: 'get.place-value-swap',
    label: 'Plaatswaarde verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling verwisselt tientallen en eenheden, of honderdtallen en tientallen.',
    typicalFamilies: ['GET-GROOT-LEZEN', 'GET-GROOT-POS'],
  },
  {
    id: 'get.decimal-align',
    label: 'Komma niet uitgelijnd',
    module: 'Getalbegrip',
    description:
      'Leerling lijnt kommagetallen niet uit bij optellen/aftrekken.',
    typicalFamilies: ['GET-KOMMA-PLUS', 'GET-KOMMA-MIN'],
  },
  {
    id: 'get.fraction-whole-comparison',
    label: 'Breuk als geheel getal vergeleken',
    module: 'Getalbegrip',
    description:
      'Leerling vergelijkt teller en noemer apart i.p.v. de breukwaarde.',
    typicalFamilies: ['GET-BREUK-BASIS', 'GET-VERGELIJK'],
  },
  {
    id: 'get.percentage-of-vs-increase',
    label: 'Percentage van vs. toename',
    module: 'Getalbegrip',
    description:
      'Leerling verwart "X% van" met "X% toename" (bijv. 20% van 100 vs. 100 + 20%).',
    typicalFamilies: ['GET-PROC-VAN', 'GET-PROC-TOENAME'],
  },
  {
    id: 'get.negative-subtraction-direction',
    label: 'Negatief aftrekken omgedraaid',
    module: 'Getalbegrip',
    description:
      'Leerling draait de richting om bij aftrekken met negatieve getallen.',
    typicalFamilies: ['GET-NEGATIEF-MIN'],
  },

  // ── Wortels & Machten ─────────────────────────────────────────────
  {
    id: 'get.wortel-halveren',
    label: 'Wortel als halveren',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat √n hetzelfde is als n ÷ 2 (bv. √16 = 8 i.p.v. 4).',
    typicalFamilies: ['GET-WORTEL'],
  },
  {
    id: 'get.wortel-verdubbelen',
    label: 'Wortel als verdubbelen of × 2',
    module: 'Getalbegrip',
    description: 'Leerling denkt dat √n hetzelfde is als n × 2 (bv. √25 = 50).',
    typicalFamilies: ['GET-WORTEL'],
  },
  {
    id: 'get.wortel-kwadraat-verwarring',
    label: 'Wortel verwarren met kwadraat',
    module: 'Getalbegrip',
    description:
      'Leerling berekent het kwadraat (n × n) i.p.v. de wortel (zijn omgekeerde).',
    typicalFamilies: ['GET-WORTEL'],
  },
  {
    id: 'get.wortel-vergelijken-fout',
    label: 'Wortels vergelijken fout (ordening omgekeerd)',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat een groter getal onder √ een kleinere wortel geeft, of berekent niet en gokt.',
    typicalFamilies: ['GET-WORTEL'],
  },
  {
    id: 'get.wortel-bewerkingsfout',
    label: 'Verkeerde bewerking bij wortelteken',
    module: 'Getalbegrip',
    description:
      'Leerling past optellen, aftrekken of een andere niet-wortel-bewerking toe op het getal onder √.',
    typicalFamilies: ['GET-WORTEL'],
  },

  // ── BTW / Procenten — exclusief ↔ inclusief ───────────────────────
  {
    id: 'get.btw-procent-van-inclusief',
    label: 'BTW % over inclusief-prijs berekend',
    module: 'Getalbegrip',
    description:
      'Leerling neemt 21% van de prijs inclusief BTW i.p.v. eerst delen door 1,21.',
    typicalFamilies: ['GET-PROC-BTW'],
  },
  {
    id: 'get.btw-trekken-procent',
    label: 'BTW als percentage-aftrek',
    module: 'Getalbegrip',
    description:
      'Leerling trekt simpelweg het percentage van de inclusief-prijs af (-21%) i.p.v. delen door 1,21.',
    typicalFamilies: ['GET-PROC-BTW'],
  },
  {
    id: 'get.btw-excl-als-btw-bedrag',
    label: 'Prijs exclusief verward met BTW-bedrag',
    module: 'Getalbegrip',
    description:
      'Leerling geeft de prijs exclusief BTW als antwoord op de vraag naar het BTW-bedrag (of omgekeerd).',
    typicalFamilies: ['GET-PROC-BTW'],
  },
  {
    id: 'get.btw-percentage-als-bedrag',
    label: 'Percentage als eurobedrag',
    module: 'Getalbegrip',
    description:
      'Leerling telt het percentage (b.v. €21) als bedrag op i.p.v. 21% × prijs.',
    typicalFamilies: ['GET-PROC-BTW'],
  },
  {
    id: 'get.btw-tarief-verwisseld',
    label: 'BTW-tarief verwisseld (9% vs 21%)',
    module: 'Getalbegrip',
    description:
      'Leerling gebruikt het verkeerde BTW-tarief of past 21% toe bij een 9%-product.',
    typicalFamilies: ['GET-PROC-BTW'],
  },
  {
    id: 'get.btw-deeltotaal-als-eind',
    label: 'Subtotaal exclusief als eindbedrag',
    module: 'Getalbegrip',
    description:
      'Leerling geeft het totaal exclusief BTW als eindprijs voor de klant.',
    typicalFamilies: ['GET-PROC-BTW'],
  },

  // ── Verhoudingen: Verdelen ─────────────────────────────────────────
  {
    id: 'get.verh-verdelen-door-verkeerd-aantal-delen',
    label: 'Deelt door verkeerd aantal delen',
    module: 'Getalbegrip',
    description:
      'Leerling deelt door het aantal delen van één groep (bv. ÷3 bij 2:3) i.p.v. door het totaal (2+3=5).',
    typicalFamilies: ['GET-VERH-VERDELEN'],
  },
  {
    id: 'get.verh-verdelen-halveert',
    label: 'Halveert in plaats van verhouding',
    module: 'Getalbegrip',
    description:
      'Leerling halveert het totaal i.p.v. de verhouding te gebruiken (denkt: gelijke verdeling).',
    typicalFamilies: ['GET-VERH-VERDELEN'],
  },
  {
    id: 'get.verh-verdelen-grootste-kleinste-verwisseld',
    label: 'Grootste en kleinste deel verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling geeft het kleinste deel als antwoord op de vraag naar het grootste deel (of andersom).',
    typicalFamilies: ['GET-VERH-VERDELEN'],
  },
  {
    id: 'get.verh-verdelen-totaal-keer-deel',
    label: 'Totaal vermenigvuldigd met deel',
    module: 'Getalbegrip',
    description:
      'Leerling vermenigvuldigt het totaal met het aantal delen i.p.v. eerst 1 deel te berekenen.',
    typicalFamilies: ['GET-VERH-VERDELEN'],
  },
  {
    id: 'get.verh-verdelen-deelt-door-een-deel',
    label: 'Deelt door één verhoudingsgetal',
    module: 'Getalbegrip',
    description:
      'Leerling deelt door één van de verhoudingsgetallen (bv. ÷2 of ÷3) i.p.v. door de som.',
    typicalFamilies: ['GET-VERH-VERDELEN'],
  },

  // ── Verhoudingen: Recept ───────────────────────────────────────────
  {
    id: 'get.verh-recept-vermenigvuldigt-totaal',
    label: 'Vermenigvuldigt totaal i.p.v. per stuk',
    module: 'Getalbegrip',
    description:
      'Leerling vermenigvuldigt de oorspronkelijke hoeveelheid met het nieuwe aantal i.p.v. eerst per stuk te berekenen.',
    typicalFamilies: ['GET-VERH-RECEPT'],
  },
  {
    id: 'get.verh-recept-verdubbelt',
    label: 'Verdubbelt altijd',
    module: 'Getalbegrip',
    description:
      'Leerling verdubbelt de hoeveelheid ongeacht de factor (denkt: meer = ×2).',
    typicalFamilies: ['GET-VERH-RECEPT'],
  },
  {
    id: 'get.verh-recept-factor-fout',
    label: 'Verkeerde factor gebruikt',
    module: 'Getalbegrip',
    description:
      'Leerling gebruikt een verkeerde factor (bv. ÷ i.p.v. ×, of telt op i.p.v. vermenigvuldigt).',
    typicalFamilies: ['GET-VERH-RECEPT'],
  },
  {
    id: 'get.verh-recept-per-stuk-onjuist',
    label: 'Berekening per stuk onjuist',
    module: 'Getalbegrip',
    description:
      'Leerling berekent een onjuiste hoeveelheid per persoon/stuk (rekenfout in de deling).',
    typicalFamilies: ['GET-VERH-RECEPT'],
  },
  {
    id: 'get.verh-recept-verhouding-als-gelijk',
    label: 'Verhouding als gelijk gezien',
    module: 'Getalbegrip',
    description:
      'Leerling gebruikt evenveel van beide ingrediënten i.p.v. de verhouding (bv. 3:1).',
    typicalFamilies: ['GET-VERH-RECEPT'],
  },

  // ── Breuken delen ─────────────────────────────────────────────────
  {
    id: 'get.breuk-deel-niet-omdraaien',
    label: 'Breuk delen zonder om te draaien',
    module: 'Getalbegrip',
    description:
      'Leerling vermenigvuldigt de breuken direct i.p.v. eerst de tweede breuk om te draaien.',
    typicalFamilies: ['GET-BREUK-DEEL'],
  },
  {
    id: 'get.breuk-deel-alleen-teller',
    label: 'Alleen teller of noemer omgedraaid',
    module: 'Getalbegrip',
    description:
      'Leerling draait alleen de teller of alleen de noemer om i.p.v. de hele tweede breuk.',
    typicalFamilies: ['GET-BREUK-DEEL'],
  },
  {
    id: 'get.breuk-deel-noemer-als-antwoord',
    label: 'Noemer of teller direct als antwoord',
    module: 'Getalbegrip',
    description:
      'Leerling neemt de noemer (of teller) van een van de breuken direct als antwoord op een delingsvraag.',
    typicalFamilies: ['GET-BREUK-DEEL'],
  },
  {
    id: 'get.breuk-deel-omkering-vergeten',
    label: 'Omkering vergeten in tweede stap',
    module: 'Getalbegrip',
    description:
      'Leerling neemt het omgekeerde maar vergeet vervolgens te vermenigvuldigen — geeft het omgekeerde zelf als antwoord.',
    typicalFamilies: ['GET-BREUK-DEEL'],
  },
  {
    id: 'get.breuk-deel-resultaat-omkeren',
    label: 'Eindresultaat omgekeerd geschreven',
    module: 'Getalbegrip',
    description:
      'Leerling rekent correct maar wisselt teller en noemer in het eindantwoord (b.v. ½ i.p.v. 2).',
    typicalFamilies: ['GET-BREUK-DEEL'],
  },

  // ── Kommagetallen — afronden ──────────────────────────────────────
  {
    id: 'get.afronden-altijd-omlaag',
    label: 'Altijd naar beneden afronden',
    module: 'Getalbegrip',
    description:
      'Leerling rondt altijd af op het bestaande cijfer, ook bij honderdsten ≥ 5.',
    typicalFamilies: ['GET-KOMMA-AFRONDEN', 'GET-GROOT-AFRONDEN'],
  },
  {
    id: 'get.afronden-bij-5-omlaag',
    label: 'Bij precies 5 naar beneden afronden',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat 5 of meer naar beneden mag (b.v. 12,345 → 12,34).',
    typicalFamilies: ['GET-KOMMA-AFRONDEN', 'GET-GROOT-AFRONDEN'],
  },
  {
    id: 'get.afronden-aantal-decimalen-verkeerd',
    label: 'Verkeerd aantal decimalen aangehouden',
    module: 'Getalbegrip',
    description:
      'Leerling rondt af op een ander aantal decimalen dan gevraagd (b.v. 1 i.p.v. 2 decimalen).',
    typicalFamilies: ['GET-KOMMA-AFRONDEN'],
  },
  {
    id: 'get.afronden-onnodige-nul',
    label: 'Onnodige nul of formaatfout',
    module: 'Getalbegrip',
    description:
      'Leerling voegt een nul toe of laat de gevraagde decimaal weg (b.v. 12,40 i.p.v. 12,35; of 10 i.p.v. 10,0).',
    typicalFamilies: ['GET-KOMMA-AFRONDEN'],
  },
  {
    id: 'get.afronden-doorrol-vergeten',
    label: 'Doorrol bij afronden vergeten',
    module: 'Getalbegrip',
    description:
      'Leerling vergeet dat 9,95 doorloopt naar 10,0 en houdt 9,9 of schrijft 9,10.',
    typicalFamilies: ['GET-KOMMA-AFRONDEN'],
  },
  {
    id: 'get.afronden-volgend-cijfer-meerekenen',
    label: 'Naar verkeerd vervolgcijfer gekeken',
    module: 'Getalbegrip',
    description:
      'Leerling kijkt naar het verkeerde cijfer (b.v. duizendsten bij afronden op 1 decimaal).',
    typicalFamilies: ['GET-KOMMA-AFRONDEN'],
  },
  {
    id: 'get.afronden-eenmalig-vs-stapsgewijs',
    label: 'Stapsgewijs afronden vs. eenmalig afronden',
    module: 'Getalbegrip',
    description:
      'Leerling rondt in één stap af terwijl stapsgewijs gevraagd wordt, of andersom.',
    typicalFamilies: ['GET-KOMMA-AFRONDEN'],
  },
  {
    id: 'get.afronden-tussenresultaat-als-eind',
    label: 'Tussenresultaat als eindantwoord',
    module: 'Getalbegrip',
    description:
      'Leerling stopt na de eerste afrondstap en geeft het tussenresultaat als eindantwoord.',
    typicalFamilies: ['GET-KOMMA-AFRONDEN'],
  },

  // ── Schatten — vermenigvuldigen, optellen, aftrekken ──────────────
  {
    id: 'get.schatten-een-factor-afgerond',
    label: 'Slechts één factor afgerond bij schatten',
    module: 'Getalbegrip',
    description:
      'Leerling rondt maar één van de twee getallen af bij schatten van een vermenigvuldiging (bv. 48 × 21 → 48 × 20 in plaats van 50 × 20).',
    typicalFamilies: ['GET-GROOT-SCHATTEN'],
  },
  {
    id: 'get.schatten-te-ver-afgerond',
    label: 'Te ver afgerond bij schatten',
    module: 'Getalbegrip',
    description:
      'Leerling rondt een getal te ver af naar een minder nauwkeurig veelvoud (bv. 48 → 40 in plaats van 50), waardoor de schatting fors afwijkt.',
    typicalFamilies: ['GET-GROOT-SCHATTEN'],
  },
  {
    id: 'get.schatten-verkeerd-dichtstbijzijnde',
    label: 'Verkeerd dichtstbijzijnde veelvoud gekozen',
    module: 'Getalbegrip',
    description:
      'Leerling rondt naar het verkeerde dichtstbijzijnde veelvoud (bv. 21 → 25 i.p.v. 20, of 312 → 350 i.p.v. 300).',
    typicalFamilies: ['GET-GROOT-SCHATTEN'],
  },
  {
    id: 'get.schatten-verkeerde-richting',
    label: 'Verkeerde richting bij afronden binnen schatting',
    module: 'Getalbegrip',
    description:
      'Leerling rondt in de verkeerde richting binnen een schatting (bv. 489 → 400 i.p.v. 500 hoewel het tientallencijfer ≥ 5 is).',
    typicalFamilies: ['GET-GROOT-SCHATTEN'],
  },
  {
    id: 'get.schatten-verkeerd-niveau',
    label: 'Afgerond op verkeerd niveau (tientallen i.p.v. honderdtallen)',
    module: 'Getalbegrip',
    description:
      'Leerling rondt af op een te fijn niveau (bv. tientallen 310 + 490) waar de opgave vraagt om handige honderdtallen-schatting.',
    typicalFamilies: ['GET-GROOT-SCHATTEN'],
  },

  // ── Breuken — basis (lezen, tellen, teller/noemer) ────────────────
  {
    id: 'get.fraction-count-error',
    label: 'Verkeerd aantal stukken geteld',
    module: 'Getalbegrip',
    description:
      'Leerling telt te veel of te weinig stukken bij het bepalen van de teller (b.v. 2 i.p.v. 3 gekleurde stukken).',
    typicalFamilies: ['GET-BREUK-BASIS', 'GET-BREUK-RASTER', 'GET-BREUK-LEZEN'],
  },
  {
    id: 'get.fraction-teller-noemer-swap',
    label: 'Teller en noemer verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling schrijft de noemer als teller (b.v. ⁴⁄₃ i.p.v. ³⁄₄).',
    typicalFamilies: ['GET-BREUK-BASIS', 'GET-BREUK-LEZEN', 'GET-BREUK-RASTER'],
  },
  {
    id: 'get.fraction-whole-confusion',
    label: 'Breuk als geheel getal gelezen',
    module: 'Getalbegrip',
    description:
      'Leerling leest de teller of noemer los als geheel getal (b.v. ¾ als 3 of 4).',
    typicalFamilies: ['GET-BREUK-BASIS', 'GET-BREUK-LEZEN'],
  },
  {
    id: 'get.fraction-remaining-vs-eaten',
    label: 'Overgebleven vs. opgegeten verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling kiest het overgebleven deel als antwoord terwijl naar het opgegeten/gebruikte deel gevraagd wordt (of omgekeerd).',
    typicalFamilies: ['GET-BREUK-BASIS'],
  },
  {
    id: 'get.fraction-wrong-total',
    label: 'Verkeerd totaal als noemer',
    module: 'Getalbegrip',
    description:
      'Leerling neemt een verkeerd aantal als totaal (b.v. alleen de rest, of teller + 1) als noemer.',
    typicalFamilies: ['GET-BREUK-BASIS', 'GET-BREUK-RASTER'],
  },

  // ── Breuken — vergelijken ─────────────────────────────────────────
  {
    id: 'get.fraction-compare-wrong-pick',
    label: 'Kleinere breuk als grootste gekozen',
    module: 'Getalbegrip',
    description:
      'Leerling kiest de breuk met de grootste noemer (of teller) als grootste, zonder de waarde te berekenen.',
    typicalFamilies: ['GET-VERGELIJK', 'GET-VERGELIJK-JUDGE'],
  },
  {
    id: 'get.fraction-compare-equal-error',
    label: 'Ongelijke breuken als gelijk beoordeeld',
    module: 'Getalbegrip',
    description:
      'Leerling concludeert dat twee breuken gelijk zijn op basis van teller/noemer-patronen i.p.v. waarde.',
    typicalFamilies: ['GET-VERGELIJK', 'GET-VERGELIJK-JUDGE'],
  },
  {
    id: 'get.fraction-compare-give-up',
    label: 'Geeft op bij ongelijknamige breuken',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat ongelijknamige breuken niet vergelijkbaar zijn en kiest willekeurig of "gelijk".',
    typicalFamilies: ['GET-VERGELIJK', 'GET-VERGELIJK-JUDGE'],
  },

  // ── Procenten — 1%-methode en strategie-fouten ────────────────────
  {
    id: 'get.percentage-divide-confusion',
    label: 'Verkeerd gedeeld bij %-berekening',
    module: 'Getalbegrip',
    description:
      'Leerling deelt door een verkeerd getal (b.v. door 10 i.p.v. 100) bij het toepassen van de 1%- of 10%-methode.',
    typicalFamilies: ['GET-PROC-VAN', 'GET-PROC-BASIS'],
  },
  {
    id: 'get.percentage-swap-method',
    label: 'Tussenstap verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling verwisselt tussenstappen bij de 10%/5%/1%-methode (b.v. 10% + 5% verwisseld).',
    typicalFamilies: ['GET-PROC-VAN', 'GET-PROC-BASIS'],
  },
  {
    id: 'get.percentage-round-error',
    label: 'Afrondfout bij %-berekening',
    module: 'Getalbegrip',
    description:
      'Leerling rondt een tussenresultaat te vroeg of verkeerd af bij %-berekeningen.',
    typicalFamilies: ['GET-PROC-VAN', 'GET-PROC-BASIS', 'GET-PROC-BTW'],
  },

  // ── Procenten — basis (breuk-procent-decimaal koppeling) ──────────
  {
    id: 'get.kwart-helft-verward',
    label: 'Kwart en helft verwisseld',
    module: 'Getalbegrip',
    description: 'Leerling verwart 25% (¼) met 50% (½).',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-VERH-BREUK-PROC-KOPPEL'],
  },
  {
    id: 'get.kwart-tiende-verward',
    label: 'Kwart en tiende verwisseld',
    module: 'Getalbegrip',
    description: 'Leerling verwart 25% (¼) met 10% (1/10).',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-VERH-BREUK-PROC-KOPPEL'],
  },
  {
    id: 'get.deel-rest-verward',
    label: 'Deel en rest verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling kiest het complement (b.v. ¾ i.p.v. ¼) door verkeerd af te lezen welke kant gevraagd wordt.',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-BREUK-BASIS'],
  },
  {
    id: 'get.decimaal-komma-verschuiving',
    label: 'Komma één plek verschoven bij %-omzetting',
    module: 'Getalbegrip',
    description:
      'Leerling schuift de komma één plek te ver/te weinig bij omzetting decimaal ↔ procent (0,5 → 5% i.p.v. 50%).',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-VERH-BREUK-PROC-KOPPEL'],
  },
  {
    id: 'get.decimaal-is-procent',
    label: 'Decimaal direct als procent gelezen',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat een decimaal getal direct een percentage is (0,5 = 0,5%).',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-VERH-BREUK-PROC-KOPPEL'],
  },
  {
    id: 'get.decimaal-keer-1000',
    label: 'Decimaal × 1000 i.p.v. × 100',
    module: 'Getalbegrip',
    description:
      'Leerling vermenigvuldigt een decimaal met 1000 i.p.v. 100 bij omzetting naar procent.',
    typicalFamilies: ['GET-PROC-BASIS'],
  },
  {
    id: 'get.aantal-is-procent',
    label: 'Aantal direct als percentage gelezen',
    module: 'Getalbegrip',
    description:
      'Leerling neemt het aantal stukken (b.v. 5) direct als percentage (5%) zonder te relateren aan het totaal.',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-PROC-VAN'],
  },
  {
    id: 'get.deel-helft-verward',
    label: 'Willekeurig deel als helft gelezen',
    module: 'Getalbegrip',
    description:
      'Leerling neemt een willekeurig deel/fractie aan als de helft (50%).',
    typicalFamilies: ['GET-PROC-BASIS'],
  },
  {
    id: 'get.totaal-is-procent',
    label: 'Totaal direct als percentage gelezen',
    module: 'Getalbegrip',
    description:
      'Leerling neemt het totaal aantal (b.v. 20) direct als percentage (20%).',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-PROC-VAN'],
  },
  {
    id: 'get.procent-is-aantal',
    label: 'Percentage als aantal stuks gelezen',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat 75% gelijk is aan 75 stuks, zonder rekening te houden met het totaal.',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-PROC-VAN'],
  },
  {
    id: 'get.procent-deel-10',
    label: 'Procent gedeeld door 10 i.p.v. 100',
    module: 'Getalbegrip',
    description:
      'Leerling deelt het percentage door 10 i.p.v. 100 (75% van 100 → 7,5).',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-PROC-VAN'],
  },
  {
    id: 'get.driekwart-kwart-verward',
    label: 'Driekwart en kwart verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling verwart 75% (¾) met 25% (¼), vaak omdat naar de rest gekeken wordt.',
    typicalFamilies: ['GET-PROC-BASIS'],
  },
  {
    id: 'get.percent-fraction-swap',
    label: 'Percentage-breuk koppeling omgedraaid',
    module: 'Getalbegrip',
    description:
      'Leerling koppelt het verkeerde percentage aan een breuk (75% ↔ ¼) door teller-noemer-verwisseling.',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-VERH-BREUK-PROC-KOPPEL'],
  },
  {
    id: 'get.percent-fraction-approx',
    label: 'Breuk bij benadering gekozen',
    module: 'Getalbegrip',
    description:
      'Leerling kiest een breuk die alleen ongeveer klopt (b.v. ⅓ voor 30%) zonder exacte koppeling.',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-VERH-BREUK-PROC-KOPPEL'],
  },
  {
    id: 'get.percent-fraction-close',
    label: 'Dichtbij percentage, verkeerde breuk',
    module: 'Getalbegrip',
    description:
      'Leerling kiest een breuk die qua percentage dichtbij ligt maar niet de exacte koppeling is.',
    typicalFamilies: ['GET-PROC-BASIS', 'GET-VERH-BREUK-PROC-KOPPEL'],
  },

  // ── Machten ───────────────────────────────────────────────────────
  {
    id: 'get.macht-als-optellen',
    label: 'Machtsverheffen als optellen',
    module: 'Getalbegrip',
    description:
      'Leerling verwart aⁿ met a + a + … (n keer) i.p.v. a × a × … (n keer). Bv. 2³ = 6.',
    typicalFamilies: ['GET-MACHT', 'GET-MACHT-GRONDTAL'],
  },
  {
    id: 'get.macht-als-vermenigvuldigen',
    label: 'Machtsverheffen als grondtal × exponent',
    module: 'Getalbegrip',
    description:
      'Leerling rekent aⁿ als a × n i.p.v. herhaald vermenigvuldigen. Bv. 2³ = 6.',
    typicalFamilies: ['GET-MACHT', 'GET-MACHT-GRONDTAL'],
  },
  {
    id: 'get.exponent-als-grondtal',
    label: 'Grondtal en exponent verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling neemt de exponent als grondtal (of omgekeerd) bij berekening, bv. 2³ → 3 × 3 = 9.',
    typicalFamilies: ['GET-MACHT', 'GET-MACHT-GRONDTAL'],
  },
  {
    id: 'get.exponent-met-1-vermindert',
    label: 'Exponent één te laag genomen',
    module: 'Getalbegrip',
    description:
      'Leerling rekent met n − 1 i.p.v. n (vaak omdat de eerste factor "al aanwezig" wordt geteld). Bv. 2⁴ → 2³ = 8.',
    typicalFamilies: ['GET-MACHT'],
  },
  {
    id: 'get.hogere-exponent-altijd-groter',
    label: 'Hogere exponent altijd groter aangenomen',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat een hogere exponent altijd een grotere uitkomst geeft, ongeacht het grondtal.',
    typicalFamilies: ['GET-MACHT'],
  },
  {
    id: 'get.groter-grondtal-altijd-groter',
    label: 'Groter grondtal altijd groter aangenomen',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat een groter grondtal altijd een grotere uitkomst geeft, ongeacht de exponent.',
    typicalFamilies: ['GET-MACHT'],
  },
  {
    id: 'get.machten-niet-vergelijkbaar',
    label: 'Machten niet vergelijkbaar aangenomen',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat je machten met verschillende grondtallen of exponenten niet kunt vergelijken zonder uit te rekenen.',
    typicalFamilies: ['GET-MACHT'],
  },

  // ── Negatieve getallen — optellen ─────────────────────────────────
  {
    id: 'get.negatief-houdt-negatief',
    label: 'Antwoord moet negatief blijven',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat als er een negatief getal in de som staat, het antwoord ook negatief moet zijn (−3 + 7 → −10 of −4).',
    typicalFamilies: ['GET-NEGATIEF-PLUS', 'GET-NEGATIEF'],
  },
  {
    id: 'get.negatief-genegeerd',
    label: 'Minteken genegeerd',
    module: 'Getalbegrip',
    description:
      'Leerling negeert het minteken volledig en rekent met absolute waarden (−3 + 7 → 3 + 7 = 10).',
    typicalFamilies: ['GET-NEGATIEF-PLUS', 'GET-NEGATIEF-MIN', 'GET-NEGATIEF'],
  },
  {
    id: 'get.beide-negatief',
    label: 'Beide getallen als negatief behandeld',
    module: 'Getalbegrip',
    description:
      'Leerling behandelt beide getallen als negatief (−3 + 7 → −3 + −7).',
    typicalFamilies: ['GET-NEGATIEF-PLUS', 'GET-NEGATIEF'],
  },
  {
    id: 'get.negatief-plus-negatief-aftrekken',
    label: '+ (−a) als − (−a) gelezen',
    module: 'Getalbegrip',
    description:
      'Leerling leest + (−3) als − (−3) en draait dus het minteken om.',
    typicalFamilies: ['GET-NEGATIEF-PLUS'],
  },
  {
    id: 'get.mintekens-vallen-weg',
    label: 'Twee mintekens vallen weg-regel verkeerd toegepast',
    module: 'Getalbegrip',
    description:
      'Leerling past "min × min = plus" toe op + (−a), waardoor het minteken verdwijnt.',
    typicalFamilies: ['GET-NEGATIEF-PLUS', 'GET-NEGATIEF-MIN'],
  },
  {
    id: 'get.haakjes-negeren',
    label: 'Haakjes met minteken genegeerd',
    module: 'Getalbegrip',
    description:
      'Leerling negeert het minteken binnen haakjes (b.v. + (−3) → + 3).',
    typicalFamilies: ['GET-NEGATIEF-PLUS', 'GET-NEGATIEF-MIN'],
  },
  {
    id: 'get.absoluut-verschil-positief',
    label: 'Positief verschil als antwoord',
    module: 'Getalbegrip',
    description:
      'Leerling geeft het absolute verschil als positief antwoord, ook als het resultaat negatief moet zijn (−6 + −9 → 3).',
    typicalFamilies: ['GET-NEGATIEF-PLUS', 'GET-NEGATIEF-MIN'],
  },
  {
    id: 'get.alles-negatief',
    label: 'Alles als negatieve som',
    module: 'Getalbegrip',
    description:
      'Leerling pakt zowel het minteken als de absolute som: −(a + b) i.p.v. −a + b.',
    typicalFamilies: ['GET-NEGATIEF-PLUS'],
  },
  {
    id: 'get.saldo-positief-aanname',
    label: 'Bijstorting maakt saldo positief',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat een bijstorting het saldo altijd positief maakt, ongeacht de hoogte van het negatieve saldo.',
    typicalFamilies: ['GET-NEGATIEF-PLUS'],
  },
  {
    id: 'get.afhalen-na-bijstorten-genegeerd',
    label: 'Tweede afboeking vergeten',
    module: 'Getalbegrip',
    description:
      'Leerling vergeet een tweede afboeking in een meerstapsverhaal met saldo.',
    typicalFamilies: ['GET-NEGATIEF-PLUS'],
  },
  {
    id: 'get.bijstorten-genegeerd',
    label: 'Bijstorting vergeten',
    module: 'Getalbegrip',
    description:
      'Leerling vergeet een bijstorting in een meerstapsverhaal met saldo.',
    typicalFamilies: ['GET-NEGATIEF-PLUS'],
  },

  // ── Kommagetallen — delen ─────────────────────────────────────────
  {
    id: 'get.komma-delen-rest-niet-doorgeschoven',
    label: 'Rest niet doorgeschoven naar volgende decimaal',
    module: 'Getalbegrip',
    description:
      'Leerling deelt eenheden en tienden los en negeert de rest van de eenheden-deling (b.v. 7,5 ÷ 3 → 7÷3 = 2,3).',
    typicalFamilies: ['GET-KOMMA-DEEL'],
  },
  {
    id: 'get.komma-delen-komma-genegeerd',
    label: 'Komma weggelaten bij delen',
    module: 'Getalbegrip',
    description:
      'Leerling laat de komma weg en deelt het getal als heel getal (b.v. 7,5 ÷ 3 → 75 ÷ 3 = 25).',
    typicalFamilies: ['GET-KOMMA-DEEL'],
  },
  {
    id: 'get.komma-delen-aftrekken',
    label: 'Aftrekken i.p.v. delen',
    module: 'Getalbegrip',
    description:
      'Leerling trekt de deler af van het getal i.p.v. te delen (b.v. 7,5 ÷ 3 → 7,5 − 3 = 4,5).',
    typicalFamilies: ['GET-KOMMA-DEEL'],
  },

  // ── Breuken — vermenigvuldigen ────────────────────────────────────
  {
    id: 'get.breuk-keer-optellen-tnoemer',
    label: 'Teller én noemer opgeteld i.p.v. vermenigvuldigd',
    module: 'Getalbegrip',
    description:
      'Leerling telt zowel teller als noemer op bij breuken vermenigvuldigen (b.v. ½ × ⅓ → 2/5).',
    typicalFamilies: ['GET-BREUK-KEER'],
  },
  {
    id: 'get.breuk-keer-teller-optellen',
    label: 'Teller opgeteld i.p.v. vermenigvuldigd',
    module: 'Getalbegrip',
    description:
      'Leerling telt de tellers op maar vermenigvuldigt de noemers (b.v. ½ × ⅓ → 2/6).',
    typicalFamilies: ['GET-BREUK-KEER'],
  },
  {
    id: 'get.breuk-keer-noemer-optellen',
    label: 'Noemer opgeteld i.p.v. vermenigvuldigd',
    module: 'Getalbegrip',
    description:
      'Leerling vermenigvuldigt de tellers maar telt de noemers op (b.v. ½ × ⅓ → 1/5).',
    typicalFamilies: ['GET-BREUK-KEER'],
  },

  // ── Kommagetallen — vermenigvuldigen ──────────────────────────────
  {
    id: 'get.komma-keer-decimaal-verschoven',
    label: 'Komma één plek te ver verschoven',
    module: 'Getalbegrip',
    description:
      'Leerling schuift de komma één plek te ver naar links bij het terugplaatsen (b.v. 0,3 × 4 → 0,12 i.p.v. 1,2; of 1,8 × 5 → 0,9 i.p.v. 9).',
    typicalFamilies: ['GET-KOMMA-KEER'],
  },
  {
    id: 'get.komma-keer-genegeerd',
    label: 'Komma weggelaten bij vermenigvuldigen',
    module: 'Getalbegrip',
    description:
      'Leerling rekent zonder komma en plaatst de komma niet terug (b.v. 0,3 × 4 → 12; 1,8 × 5 → 90).',
    typicalFamilies: ['GET-KOMMA-KEER'],
  },
  {
    id: 'get.komma-keer-optellen',
    label: 'Optellen i.p.v. vermenigvuldigen',
    module: 'Getalbegrip',
    description:
      'Leerling telt het kommagetal en het andere getal bij elkaar op (b.v. 0,3 × 4 → 0,3 + 0,4 = 0,7; 2,5 × 3 → 5,5).',
    typicalFamilies: ['GET-KOMMA-KEER'],
  },
  {
    id: 'get.komma-keer-tienden-plakken',
    label: 'Eenheden en tienden naast elkaar geplakt',
    module: 'Getalbegrip',
    description:
      'Leerling rekent eenheden en tienden los uit en plakt ze naast elkaar (b.v. 2,5 × 3 → 2×3=6 en 5×3=15 → 7,15 i.p.v. 7,5).',
    typicalFamilies: ['GET-KOMMA-KEER'],
  },
  {
    id: 'get.komma-keer-extra-decimaal',
    label: 'Te veel decimalen in antwoord',
    module: 'Getalbegrip',
    description:
      'Leerling telt te veel decimalen mee bij het terugplaatsen van de komma (b.v. 0,4 × 0,3 → 0,012 i.p.v. 0,12; 1,25 × 8 → 1,0 i.p.v. 10).',
    typicalFamilies: ['GET-KOMMA-KEER'],
  },
  // === GET-PROC-SAMENGESTELD ===
  {
    id: 'get.percent-compound-added',
    label: 'Kortingen/stijgingen opgeteld i.p.v. samengesteld',
    module: 'Getalbegrip',
    description:
      'Leerling telt twee opeenvolgende percentages bij elkaar op (bv. 20% + 10% = 30%) i.p.v. ze stap voor stap toe te passen op de tussenliggende prijs.',
    typicalFamilies: ['GET-PROC-SAMENGESTELD'],
  },
  {
    id: 'get.percent-second-on-original',
    label: 'Tweede percentage op originele prijs',
    module: 'Getalbegrip',
    description:
      'Leerling past de tweede korting/stijging toe op de oorspronkelijke prijs i.p.v. op de nieuwe (tussenliggende) prijs.',
    typicalFamilies: ['GET-PROC-SAMENGESTELD'],
  },
  {
    id: 'get.percent-step-omitted',
    label: 'Een van de samengestelde stappen overgeslagen',
    module: 'Getalbegrip',
    description:
      'Leerling rekent slechts één van twee opeenvolgende percentage-bewerkingen uit en stopt bij de tussenprijs.',
    typicalFamilies: ['GET-PROC-SAMENGESTELD'],
  },
  {
    id: 'get.percent-increase-decrease-symmetric',
    label: '+x% en −x% beschouwd als gelijk aan beginprijs',
    module: 'Getalbegrip',
    description:
      'Leerling denkt dat +x% gevolgd door −x% (of omgekeerd) uitkomt op de oorspronkelijke prijs — over het hoofd ziend dat de tweede bewerking over een ander bedrag gaat.',
    typicalFamilies: ['GET-PROC-SAMENGESTELD'],
  },
  {
    id: 'get.percent-amount-as-final',
    label: 'Korting/stijgingsbedrag als eindprijs',
    module: 'Getalbegrip',
    description:
      'Leerling geeft het bedrag van de korting of stijging zelf als eindprijs, in plaats van het van de oorspronkelijke prijs af te trekken of erbij op te tellen.',
    typicalFamilies: ['GET-PROC-SAMENGESTELD', 'GET-PROC-KORTING'],
  },
  {
    id: 'get.percentage-of-wrong-base',
    label: 'Percentage van verkeerd basisbedrag',
    module: 'Getalbegrip',
    description:
      'Leerling berekent het percentage over een verkeerd basisbedrag (bv. afgerond getal, andere prijs uit de context) i.p.v. over het juiste bedrag.',
    typicalFamilies: [
      'GET-PROC-VAN',
      'GET-PROC-KORTING',
      'GET-PROC-SAMENGESTELD',
    ],
  },
]
